import type {
  CheckpointRevertResolution,
  CheckpointRevertResult,
} from '@crowdex/workspace-protocol';
import { useEffect, useRef } from 'react';
import { AddRepositoryDialog } from '@/components/add-repository-dialog';
import { BranchInWorktreeDialog } from '@/components/desktop-shell/branch-in-worktree-dialog';
import { ContinueLocallyDialog } from '@/components/desktop-shell/continue-locally-dialog';
import { CreateBranchDialog } from '@/components/desktop-shell/create-branch-dialog';
import { DivergedBranchDialog } from '@/components/desktop-shell/diverged-branch-dialog';
import { EnvironmentDialog } from '@/components/desktop-shell/environment-dialog';
import { PendingAnnotationPopover } from '@/components/desktop-shell/pending-annotation-popover';
import { PreviewBoardEffects } from '@/components/desktop-shell/preview-board-effects';
import { RewindDialog } from '@/components/desktop-shell/rewind-dialog';
import { SwitchBranchDialog } from '@/components/desktop-shell/switch-branch-dialog';
import type { ExecutionMode } from '@/components/worktree-controls';
import { FirstLaunch } from '@/features/new-conversation/first-launch';
import { WorkspaceDialogs } from '@/features/workspaces/workspace-dialogs';
import type { useEnvironmentSettings } from '@/hooks/use-environment-settings';
import { useMediaQuery } from '@/hooks/use-media-query';
import type { useWorktreeSettings } from '@/hooks/use-worktree-settings';
import { copyConversation } from '@/lib/conversations/conversation-copy-client';
import { PREVIEW_HANDOFF_DEVICES, previewBoardTitle } from '@/lib/desktop-preview-transcript';
import { branchActionsStore } from '@/lib/desktop-shell/branch-actions';
import { startConversationFromHere } from '@/lib/desktop-shell/conversation-from-here';
import { useConversationOpener } from '@/lib/desktop-shell/conversation-opener';
import { selectWorktreeSourceRoot } from '@/lib/desktop-shell/conversation-worktree';
import { DESKTOP_LAYOUT_QUERY } from '@/lib/desktop-shell/desktop-layout';
import { shownConversationState } from '@/lib/desktop-shell/store';
import { useDesktopShellStore, useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { setFolderEnvironment } from '@/lib/desktop-shell/use-workspace-environment-sync';
import type { BranchTarget, DivergedBranchChoice } from '@/lib/native-desktop';
import { sourceControlRepositoryFullName } from '@/lib/sidebar-threads';
import type { SourceControlRepository } from '@/lib/source-control-connector';

/**
 * The shell's modal/dialog layer: repository add, cloud→local handoff, branch
 * create/switch/worktree, environment settings, and agent auth. Every dialog is
 * driven by store-backed open/working/error flags read here; the imperative
 * actions (repository cloning, workspace picking, branch controls, handoff, and
 * auth) plus the environment/worktree settings hooks and derived auth data flow
 * in as props since they are not owned by the store.
 */
export function ConversationDialogs({
  preview,
  onCloneRepository,
  onOpenLocalWorkspace,
  onFocusComposer,
  pendingExecutionAfterLocalRef,
  onContinueLocally,
  onCreateBranch,
  onSwitchBranch,
  onCommitAndSwitchBranch,
  onOpenWorktreeCheckout,
  environment,
  worktreeSettings,
  onPlanRewind,
  onResendEdited,
}: {
  preview: boolean;
  onCloneRepository: (repository: SourceControlRepository) => Promise<boolean>;
  onOpenLocalWorkspace: () => Promise<boolean>;
  onFocusComposer: () => void;
  pendingExecutionAfterLocalRef: { current: ExecutionMode | null };
  onContinueLocally: () => void;
  /** Hands the conversation to a folder of another machine of the session. */
  onCreateBranch: () => void;
  onSwitchBranch: (
    target: BranchTarget,
    stash?: boolean,
    diverged?: DivergedBranchChoice | null,
  ) => void | Promise<void>;
  onCommitAndSwitchBranch: (
    target: BranchTarget,
    message: string,
    paths?: readonly string[],
  ) => void | Promise<void>;
  onOpenWorktreeCheckout: (path: string) => void | Promise<void>;
  environment: ReturnType<typeof useEnvironmentSettings>;
  worktreeSettings: ReturnType<typeof useWorktreeSettings>;
  /** What restoring the files the turns after the shown conversation's edited message changed would put back. */
  onPlanRewind: (editedIndex: number) => Promise<CheckpointRevertResult | null>;
  /**
   * Resends the shown conversation's edited message, restoring the files later turns changed first
   * (unless `null`): a restore refused over undecided conflicts returns them, sending nothing.
   */
  onResendEdited: (
    restore: CheckpointRevertResolution | null,
  ) => Promise<CheckpointRevertResult | null>;
}) {
  const chatId = useDesktopShellStore((state) => state.chatId);
  const pendingRewind = useDesktopShellStore(
    (state) => shownConversationState(state).pendingRewind,
  );
  const setConversationState = useDesktopShellStore((state) => state.setConversationState);
  const store = useDesktopShellStoreApi();
  const opener = useConversationOpener();
  const generatedTitle = useDesktopShellStore((state) => state.generatedConversationTitle);
  // The handoff names the conversation it moves (D50): the preview's, as its board does.
  const conversationTitle = preview ? (previewBoardTitle() ?? generatedTitle) : generatedTitle;
  // The header's branch chip switches and creates branches through these dialogs (D45).
  const switchBranchRef = useRef(onSwitchBranch);
  switchBranchRef.current = onSwitchBranch;
  useEffect(() => {
    branchActionsStore.setState({
      switchBranch: (target) => switchBranchRef.current(target),
      createBranch: () => store.getState().setCreateBranchOpen(true),
    });
    return () => branchActionsStore.setState({ switchBranch: null, createBranch: null });
  }, [store]);
  // A phone picks the computer (mobile 45); the account's computers are not listed yet outside
  // the preview.
  const phone = !useMediaQuery(DESKTOP_LAYOUT_QUERY);
  const handoffDevices = preview && phone ? PREVIEW_HANDOFF_DEVICES : undefined;
  const native = useDesktopShellStore((state) => state.native);
  const addRepositoryOpen = useDesktopShellStore((state) => state.addRepositoryOpen);
  const setAddRepositoryOpen = useDesktopShellStore((state) => state.setAddRepositoryOpen);
  const agents = useDesktopShellStore((state) => state.agents);
  const discoveringAgents = useDesktopShellStore((state) => state.discoveringAgents);
  const desktopLayout = useMediaQuery(DESKTOP_LAYOUT_QUERY);
  const localHandoffAgentId = useDesktopShellStore((state) => state.localHandoffAgentId);
  const agentHandoffError = useDesktopShellStore((state) => state.agentHandoffError);
  const localHandoffOpen = useDesktopShellStore((state) => state.localHandoffOpen);
  const localHandoffWorking = useDesktopShellStore((state) => state.localHandoffWorking);
  const setLocalHandoffOpen = useDesktopShellStore((state) => state.setLocalHandoffOpen);
  const setLocalHandoffAgentId = useDesktopShellStore((state) => state.setLocalHandoffAgentId);
  const setAgentHandoffError = useDesktopShellStore((state) => state.setAgentHandoffError);
  const workspace = useDesktopShellStore((state) => state.workspace);
  const gitStatus = useDesktopShellStore((state) => state.gitStatus);
  const newBranchName = useDesktopShellStore((state) => state.newBranchName);
  const branchError = useDesktopShellStore((state) => state.branchError);
  const createBranchOpen = useDesktopShellStore((state) => state.createBranchOpen);
  const setNewBranchName = useDesktopShellStore((state) => state.setNewBranchName);
  const setCreateBranchOpen = useDesktopShellStore((state) => state.setCreateBranchOpen);
  const setBranchError = useDesktopShellStore((state) => state.setBranchError);
  const pendingBranchSwitch = useDesktopShellStore((state) => state.pendingBranchSwitch);
  const setPendingBranchSwitch = useDesktopShellStore((state) => state.setPendingBranchSwitch);
  const switchingBranch = useDesktopShellStore(
    (state) => shownConversationState(state).switchingBranch,
  );
  const divergedBranch = useDesktopShellStore((state) => state.divergedBranch);
  const setDivergedBranch = useDesktopShellStore((state) => state.setDivergedBranch);
  const branchInWorktree = useDesktopShellStore((state) => state.branchInWorktree);
  const setBranchInWorktree = useDesktopShellStore((state) => state.setBranchInWorktree);
  const workspaceEnvironment = useDesktopShellStore((state) => state.workspaceEnvironment);
  const worktreeSourceRoot = useDesktopShellStore(selectWorktreeSourceRoot);
  const conversationFiles = useDesktopShellStore((state) => state.conversationFiles);

  return (
    <>
      {/* New assistant (lot 7). */}
      <WorkspaceDialogs />
      {/* The first launch's steps; its repository step opens Add a repository. */}
      <FirstLaunch
        agentNames={agents.map((candidate) => candidate.name)}
        discoveringAgents={discoveringAgents}
        onChooseRepository={() => setAddRepositoryOpen(true)}
        phone={!desktopLayout}
        preview={preview}
      />
      <AddRepositoryDialog
        localAvailable={native === true}
        onClone={onCloneRepository}
        onOpenChange={setAddRepositoryOpen}
        onOpenLocal={onOpenLocalWorkspace}
        open={addRepositoryOpen}
      />

      <PendingAnnotationPopover onFocusComposer={onFocusComposer} />

      {preview && <PreviewBoardEffects environment={environment} />}

      <RewindDialog
        files={pendingRewind?.files ?? []}
        onCancel={() => {
          pendingRewind?.cancel();
          setConversationState(chatId, { pendingRewind: null });
        }}
        onPlan={() =>
          pendingRewind === null ? Promise.resolve(null) : onPlanRewind(pendingRewind.editedIndex)
        }
        onSend={async ({ restore, mode }) => {
          if (mode === 'here') return await onResendEdited(restore);
          if (pendingRewind === null) return null;
          await startConversationFromHere({
            store,
            conversationId: chatId,
            opener,
            rewind: pendingRewind,
            copy: copyConversation,
          });
          return null;
        }}
        open={pendingRewind !== null}
        text={pendingRewind?.message.text ?? ''}
        turnCount={pendingRewind?.turnCount ?? 0}
      />

      <ContinueLocallyDialog
        agentName={agents.find((candidate) => candidate.id === localHandoffAgentId)?.name ?? null}
        branch={gitStatus?.branch ?? null}
        conversationTitle={conversationTitle}
        {...(handoffDevices === undefined
          ? {}
          : {
              devices: handoffDevices,
              // The conversation's own address, to open on the computer instead.
              onCopyLink: () =>
                void navigator.clipboard?.writeText(
                  `${window.location.origin}/?threadId=${encodeURIComponent(store.getState().chatId)}`,
                ),
            })}
        error={agentHandoffError}
        onCancel={() => {
          pendingExecutionAfterLocalRef.current = null;
          setLocalHandoffOpen(false);
        }}
        onContinue={onContinueLocally}
        onOpenChange={(open) => {
          if (localHandoffWorking) return;
          setLocalHandoffOpen(open);
          if (!open) {
            pendingExecutionAfterLocalRef.current = null;
            setLocalHandoffAgentId(null);
            setAgentHandoffError(null);
          }
        }}
        open={localHandoffOpen}
        repositoryName={sourceControlRepositoryFullName(workspace) ?? null}
        working={localHandoffWorking}
      />

      <CreateBranchDialog
        baseBranch={gitStatus?.branch ?? null}
        branchName={newBranchName}
        error={branchError}
        onBranchNameChange={setNewBranchName}
        onCreate={onCreateBranch}
        onOpenChange={(open) => {
          setCreateBranchOpen(open);
          if (!open) setBranchError(null);
        }}
        open={createBranchOpen}
        preview={preview}
      />

      <SwitchBranchDialog
        additions={gitStatus?.additions ?? 0}
        branch={
          pendingBranchSwitch === null
            ? null
            : pendingBranchSwitch.remote
              ? `origin/${pendingBranchSwitch.branch}`
              : pendingBranchSwitch.branch
        }
        deletions={gitStatus?.deletions ?? 0}
        error={branchError}
        onConfirm={() => {
          if (pendingBranchSwitch !== null) void onSwitchBranch(pendingBranchSwitch);
        }}
        onCommitConfirm={(message, paths) => {
          if (pendingBranchSwitch !== null) {
            void onCommitAndSwitchBranch(pendingBranchSwitch, message, paths);
          }
        }}
        conversationFiles={conversationFiles}
        workspace={preview ? null : workspace}
        onOpenChange={(open) => {
          if (!open) {
            setPendingBranchSwitch(null);
            setBranchError(null);
          }
        }}
        onStashConfirm={() => {
          if (pendingBranchSwitch !== null) void onSwitchBranch(pendingBranchSwitch, true);
        }}
        filesChanged={gitStatus?.filesChanged ?? 0}
        open={pendingBranchSwitch !== null}
        working={switchingBranch}
      />

      <DivergedBranchDialog
        diverged={divergedBranch}
        onChoose={(choice) => {
          if (divergedBranch === null) return;
          void onSwitchBranch(
            { branch: divergedBranch.branch, remote: true },
            divergedBranch.stash,
            choice,
          );
        }}
        onOpenChange={(open) => {
          if (!open) setDivergedBranch(null);
        }}
        working={switchingBranch}
      />

      <BranchInWorktreeDialog
        branch={branchInWorktree?.branch ?? null}
        onOpenChange={(open) => {
          if (!open) setBranchInWorktree(null);
        }}
        onOpenWorktree={() => {
          if (branchInWorktree !== null) void onOpenWorktreeCheckout(branchInWorktree.path);
        }}
        open={branchInWorktree !== null}
        working={switchingBranch}
        worktreePath={branchInWorktree?.path ?? null}
      />

      <EnvironmentDialog
        environment={environment}
        preview={preview}
        setWorkspaceEnvironment={(root, written) => setFolderEnvironment(store, root, written)}
        workspace={workspace}
        workspaceEnvironment={workspaceEnvironment}
        worktreeSettings={worktreeSettings}
        worktreeSourceRoot={worktreeSourceRoot}
      />
    </>
  );
}
